import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import type { FormContextType, WidgetProps, RJSFSchema, StrictRJSFSchema } from '@rjsf/utils';
import useLinkifiedLabel from './useLinkifiedLabel';

const selectValue = (value: any, selected: any, all: any) => {
  const at = all.indexOf(value);
  const updated = selected.slice(0, at).concat(value, selected.slice(at));

  // As inserting values at predefined index positions doesn't work with empty
  // arrays, we need to reorder the updated selection to match the initial order
  return updated.sort((a: any, b: any) => all.indexOf(a) > all.indexOf(b));
};

const deselectValue = (value: any, selected: any) => {
  return selected.filter((v: any) => v !== value);
};

/** The `CheckboxesWidget` is a widget for rendering checkbox groups.
 *  It is typically used to represent an array of enums.
 *
 * @param props - The `WidgetProps` for this component
 */
export default function CheckboxesWidget<T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>({
  schema,
  label,
  id,
  disabled,
  options,
  value,
  readonly,
  required,
  onChange,
  onBlur,
  onFocus,
}: WidgetProps<T, S, F>) {
  const { enumOptions, enumDisabled, inline } = options;

  const _onChange =
    (option: any) =>
    ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
      const all = (enumOptions as any).map(({ value: val }: any) => val);

      if (checked) {
        onChange(selectValue(option.value, value, all));
      } else {
        onChange(deselectValue(option.value, value));
      }
    };

  const _onBlur = ({ target: { value: val } }: React.FocusEvent<HTMLButtonElement>) => onBlur(id, val);
  const _onFocus = ({ target: { value: val } }: React.FocusEvent<HTMLButtonElement>) => onFocus(id, val);

  const displayLabel = useLinkifiedLabel(label);

  return (
    <>
      <FormLabel required={required} htmlFor={id}>
        {displayLabel || schema.title}
      </FormLabel>
      <FormGroup id={id} row={Boolean(inline)}>
        {Array.isArray(enumOptions) &&
          enumOptions.map((option, index: number) => {
            const checked = value.indexOf(option.value) !== -1;
            const itemDisabled = Array.isArray(enumDisabled) && enumDisabled.indexOf(option.value) !== -1;
            const checkbox = (
              <Checkbox
                inputProps={{ 'data-testid': 'root_test_checkboxes_field' }}
                id={`${id}-${option.value}`}
                name={id}
                checked={checked}
                disabled={disabled || itemDisabled || readonly}
                onChange={_onChange(option)}
                onBlur={_onBlur}
                onFocus={_onFocus}
              />
            );
            return <FormControlLabel control={checkbox} key={option.value} label={option.label} />;
          })}
      </FormGroup>
    </>
  );
}
